import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import {
  getCartPromotionsByProduct,
  mergePromotionListWithFallback,
  applyPromotionAppliedOverride,
} from "../../data/promotions";

const collectArraysDeep = (node, depth, acc, maxDepth) => {
  if (node == null || depth > maxDepth) return;
  if (Array.isArray(node)) {
    acc.push(node);
    return;
  }
  if (typeof node !== "object") return;
  for (const v of Object.values(node)) {
    collectArraysDeep(v, depth + 1, acc, maxDepth);
  }
};

const rowPromotionId = (row) => {
  const id =
    row?.promotion_id ??
    row?.promotionId ??
    row?.id ??
    row?.promo_id ??
    row?.promoId ??
    row?.PromotionId;
  return id != null && id !== "" ? String(id) : "";
};

const looksLikePromotionRow = (o) => {
  if (!o || typeof o !== "object" || Array.isArray(o)) return false;
  if (rowPromotionId(o)) return true;
  if (o.promotion || o.promo || o.promo_detail || o.promotion_info || o.details) return true;
  const needles = [
    "promotion_name",
    "promotion_title",
    "promo_name",
    "title",
    "name",
    "promotion_id",
    "discount",
    "offer",
    "promotion",
    "promo",
  ];
  const keys = Object.keys(o);
  const lc = keys.map((k) => k.toLowerCase());
  return lc.some((k) => needles.some((n) => k === n || k.includes(n)));
};

const pickPromotionsArray = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const d = payload.data ?? payload.Data ?? payload.result ?? payload;
  const inner =
    d && typeof d === "object" && !Array.isArray(d)
      ? d.data ?? d.Data ?? d.payload ?? null
      : null;

  const tryPaths = [
    d?.promotions,
    d?.items,
    d?.list,
    d?.records,
    d?.rows,
    d?.result,
    d?.data,
    d?.promotion_details,
    d?.promotion_details_list,
    d?.applicable_promotions,
    d?.available_promotions,
    inner?.promotions,
    inner?.items,
    inner?.list,
    inner?.PromotionList,
    inner?.promotion_list,
    d?.PromotionList,
    d?.promotion_list,
    Array.isArray(d) ? d : null,
    payload.promotions,
    payload.results,
    Array.isArray(payload) ? payload : null,
  ];

  for (const p of tryPaths) {
    if (Array.isArray(p) && p.length > 0 && looksLikePromotionRow(p[0])) {
      return p;
    }
  }

  const acc = [];
  collectArraysDeep(payload, 0, acc, 12);
  const candidates = acc.filter(
    (a) =>
      Array.isArray(a) &&
      a.length > 0 &&
      typeof a[0] === "object" &&
      a[0] !== null &&
      !Array.isArray(a[0]) &&
      looksLikePromotionRow(a[0])
  );
  candidates.sort((a, b) => b.length - a.length);
  if (candidates.length > 0) return candidates[0];

  for (const p of tryPaths) {
    if (Array.isArray(p) && p.length > 0) return p;
  }

  const anyArray = acc
    .filter(
      (a) =>
        Array.isArray(a) &&
        a.length > 0 &&
        typeof a[0] === "object" &&
        a[0] !== null &&
        !Array.isArray(a[0])
    )
    .sort((a, b) => b.length - a.length);
  return anyArray[0] || [];
};

const rowApplied = (row) => {
  if (!row || typeof row !== "object") return false;
  if (row.is_applied === true || row.applied === true || row.is_currently_applied === true)
    return true;
  if (row.is_applied === 1 || row.applied === 1 || row.is_currently_applied === 1)
    return true;
  const s = String(row.status ?? "").trim().toLowerCase();
  return s === "applied" || s === "1";
};

const rowTitle = (row) => {
  if (!row || typeof row !== "object") return "Promotion";
  const promo = row.promotion || row.promo || row.promo_detail || row.details || row.promotion_info;
  const fromNested =
    promo && typeof promo === "object"
      ? promo.promotion_name ??
        promo.promotion_title ??
        promo.title ??
        promo.name ??
        promo.promo_name
      : "";
  const head =
    row.promotion_heading ??
    row.heading ??
    row.label ??
    row.display_name ??
    row.displayName ??
    row.promotionName;
  const picked =
    head ??
    row.title ??
    row.name ??
    row.promotion_name ??
    row.promotion_title ??
    row.promo_name ??
    fromNested;
  const s = picked != null ? String(picked).trim() : "";
  if (s !== "") return s;
  const id = rowPromotionId(row);
  return id !== "" ? `Promotion #${id}` : "Promotion";
};

const ProductLinePromotionsDialog = ({
  open,
  onClose,
  productId,
  customerId,
  orderId,
  productTitle,
  platform,
  channels,
  cartItems = [],
  lineItem = null,
  orderDate = null,
  orderTime = null,
  forEdit = false,
  skipPromotionCheck = false,
  onApplyPromotion,
  fallbackPromotions = [],
}) => {
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState(null);
  const [optimisticAppliedId, setOptimisticAppliedId] = useState(null);

  useEffect(() => {
    if (!open) {
      setOptimisticAppliedId(null);
    }
  }, [open]);

  const pidKey = productId != null ? String(productId).trim() : "";
  const cidKey = customerId != null ? String(customerId).trim() : "";
  const oidKey = orderId != null ? String(orderId).trim() : "";
  const platKey = String(platform ?? "5");
  const chanKey = String(channels ?? "5");

  const cartKey = Array.isArray(cartItems) ? cartItems.length : 0;
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      "cartPromotionsByProduct",
      cidKey,
      pidKey,
      oidKey,
      platKey,
      chanKey,
      cartKey,
      orderDate,
      orderTime,
      forEdit,
    ],
    queryFn: () =>
      getCartPromotionsByProduct({
        platform: platKey,
        channels: chanKey,
        customerId: cidKey,
        productId: pidKey,
        orderId: oidKey || undefined,
        cartItems,
        lineItem,
        orderDate,
        orderTime,
        forEdit,
      }),
    enabled: open && !!cidKey && !!pidKey && !skipPromotionCheck,
  });

  const promotions = useMemo(() => {
    const fromApi = skipPromotionCheck ? [] : pickPromotionsArray(data);
    const fallback = Array.isArray(fallbackPromotions) ? fallbackPromotions : [];
    const merged = mergePromotionListWithFallback(fromApi, fallback);
    return applyPromotionAppliedOverride(merged, optimisticAppliedId);
  }, [data, fallbackPromotions, optimisticAppliedId, skipPromotionCheck]);

  const handleChangePromotion = useCallback(
    async (row) => {
      const promoId = rowPromotionId(row);
      if (!promoId || typeof onApplyPromotion !== "function") return;
      const payload = {
        id: promoId,
        title: rowTitle(row),
        rawData: row,
      };
      const previousAppliedId =
        optimisticAppliedId ??
        (() => {
          const current = promotions.find((entry) => rowApplied(entry));
          return current ? rowPromotionId(current) : null;
        })();
      setOptimisticAppliedId(promoId);
      setPendingId(promoId);
      try {
        const result = await Promise.resolve(onApplyPromotion(payload));
        const succeeded = result !== false;
        if (!succeeded) {
          setOptimisticAppliedId(previousAppliedId);
          return;
        }
        if (!skipPromotionCheck) {
          queryClient.invalidateQueries({
            queryKey: ["cartPromotionsByProduct"],
            exact: false,
          });
          refetch();
        }
      } catch {
        setOptimisticAppliedId(previousAppliedId);
      } finally {
        setPendingId(null);
      }
    },
    [onApplyPromotion, queryClient, refetch, promotions, optimisticAppliedId, skipPromotionCheck]
  );

  const subtitle = productTitle && String(productTitle).trim() !== "" ? productTitle : pidKey;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", maxHeight: "85vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          pb: 1,
          borderBottom: "1px dashed rgba(27, 26, 26, 0.42)",
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={{ xs: "1rem", sm: "1.15rem" }}>
            Promotions for product
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary", mt: -0.5 }}>
          <Iconify icon="mdi:close" width={22} height={22} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {!cidKey ? (
          <Alert severity="warning">Select a customer to view promotions.</Alert>
        ) : !skipPromotionCheck && (isLoading || (isFetching && !data)) ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: "#F8991D" }} />
          </Box>
        ) : !skipPromotionCheck && isError ? (
          <Alert severity="error">
            {(error && error.message) || "Failed to load promotions for this product."}
          </Alert>
        ) : promotions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No promotions found for this product.
          </Typography>
        ) : (
          <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            {promotions.map((row, idx) => {
              const idStr = rowPromotionId(row) || `row-${idx}`;
              const applied = rowApplied(row);
              const busy = pendingId === idStr;
              return (
                <ListItem
                  key={idStr}
                  sx={{
                    alignItems: "flex-start",
                    flexDirection: "column",
                    gap: 1,
                    border: "1px solid rgb(27, 26, 26)",
                    borderRadius: "8px",
                    py: 1.5,
                    px: 2,
                    bgcolor: applied ? "action.hover" : "background.paper",
                  }}
                  disableGutters
                >
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography fontWeight={600} fontSize="0.875rem" sx={{ flex: 1 }}>
                      {rowTitle(row)}
                    </Typography>
                    {applied ? (
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        disabled
                        sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600 }}
                      >
                        Applied
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy || typeof onApplyPromotion !== "function"}
                        onClick={() => handleChangePromotion(row)}
                        sx={{
                          flexShrink: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          backgroundColor: "#F8991D",
                          "&:hover": { backgroundColor: "#e08815" },
                        }}
                      >
                        {busy ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          "Change Promotion"
                        )}
                      </Button>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductLinePromotionsDialog;
